import { Droplets, Beaker, FileText } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'DONATION_CERTIFICATE' | 'REQUEST_RECEIPT' | 'ISSUE_RECEIPT' | 'TRACEABILITY_SHEET' | 'CROSSMATCH_REPORT' | 'BLOOD_BANK_INVOICE';
    bloodBankName: string;
    bloodBankNhcId?: string;
    donationId?: string;
    requestId?: string;
    issueId?: string;
    unitId?: string;
    bloodGroup: string;
    componentType?: string;
    donorNhcId?: string;
    donorName?: string;
    donationDate?: string;
    collectionDate?: string;
    expiryDate?: string;
    volume?: string;
    screeningStatus?: string;
    hospitalName?: string;
    hospitalNhcId?: string;
    crossmatchId?: string;
    crossmatchResult?: string;
    issuedTo?: string;
    issueDate?: string;
    processingFee?: number;
    componentFee?: number;
    crossmatchFee?: number;
    totalAmount?: number;
    amountPaid?: number;
    paymentStatus?: string;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, string> = {
  DONATION_CERTIFICATE: 'Blood Donation Certificate',
  REQUEST_RECEIPT: 'Blood Request Receipt',
  ISSUE_RECEIPT: 'Blood Issue Receipt',
  TRACEABILITY_SHEET: 'Blood Unit Traceability Sheet',
  CROSSMATCH_REPORT: 'Crossmatch Report',
  BLOOD_BANK_INVOICE: 'Blood Bank Invoice',
};

export default function BloodBankDocumentTemplate({ data }: Props) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Droplets className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {SUBTYPE_LABELS[data.documentSubtype] || 'Blood Bank Document'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Blood Bank:</span> <span className="text-gray-800 font-medium">{data.bloodBankName}</span></div>
        {data.bloodBankNhcId && <div><span className="text-gray-500">NHC ID:</span> <span className="font-mono text-gray-700">{data.bloodBankNhcId}</span></div>}
        <div><span className="text-gray-500">Blood Group:</span> <span className="text-gray-800 font-bold">{data.bloodGroup}</span></div>
        {data.componentType && <div><span className="text-gray-500">Component:</span> <span className="text-gray-800">{data.componentType}</span></div>}
        {data.donationId && <div><span className="text-gray-500">Donation ID:</span> <span className="font-mono text-gray-800">{data.donationId}</span></div>}
        {data.requestId && <div><span className="text-gray-500">Request ID:</span> <span className="font-mono text-gray-800">{data.requestId}</span></div>}
        {data.issueId && <div><span className="text-gray-500">Issue ID:</span> <span className="font-mono text-gray-800">{data.issueId}</span></div>}
        {data.unitId && <div><span className="text-gray-500">Unit ID:</span> <span className="font-mono text-gray-800">{data.unitId}</span></div>}
        {data.crossmatchId && <div><span className="text-gray-500">Crossmatch:</span> <span className="font-mono text-gray-800">{data.crossmatchId}</span></div>}
      </div>

      {/* Donor info */}
      {data.donorName && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">Donor</p>
          <p className="text-gray-800 font-medium">{data.donorName}</p>
          {data.donorNhcId && <p className="text-gray-500 font-mono text-[8px]">NHC: {data.donorNhcId}</p>}
          {data.donationDate && <p className="text-gray-600 text-[8px]">Donated: {data.donationDate}</p>}
        </div>
      )}

      {/* Collection & Processing */}
      {(data.collectionDate || data.expiryDate || data.volume || data.screeningStatus) && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
          {data.collectionDate && <div><span className="text-gray-500">Collected:</span> <span className="text-gray-800">{data.collectionDate}</span></div>}
          {data.expiryDate && <div><span className="text-gray-500">Expires:</span> <span className="text-gray-800">{data.expiryDate}</span></div>}
          {data.volume && <div><span className="text-gray-500">Volume:</span> <span className="text-gray-800">{data.volume}</span></div>}
          {data.screeningStatus && <div><span className="text-gray-500">Screening:</span> <span className={`font-bold ${data.screeningStatus === 'CLEAR' ? 'text-green-700' : 'text-amber-700'}`}>{data.screeningStatus}</span></div>}
        </div>
      )}

      {data.crossmatchResult && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">Crossmatch Result</p>
          <p className={`font-bold ${data.crossmatchResult === 'COMPATIBLE' ? 'text-green-700' : 'text-red-700'}`}>{data.crossmatchResult}</p>
        </div>
      )}

      {data.hospitalName && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <span className="text-gray-500">Issued to Hospital:</span> <span className="text-gray-800 font-medium">{data.hospitalName}</span>
          {data.hospitalNhcId && <span className="text-gray-500 ml-2 font-mono text-[8px]">{data.hospitalNhcId}</span>}
          {data.issueDate && <span className="text-gray-500 ml-2">on {data.issueDate}</span>}
        </div>
      )}

      {/* Fees */}
      {data.totalAmount !== undefined && (
        <div className="flex justify-end">
          <div className="w-52 bg-gray-50 border border-gray-200 rounded text-[9px]">
            {data.processingFee !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Processing</span><span className="font-mono text-gray-800">{fmt(data.processingFee)}</span></div>}
            {data.componentFee !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Component</span><span className="font-mono text-gray-800">{fmt(data.componentFee)}</span></div>}
            {data.crossmatchFee !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Crossmatch</span><span className="font-mono text-gray-800">{fmt(data.crossmatchFee)}</span></div>}
            <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold"><span className="text-gray-800">Total</span><span className="font-mono text-gray-900">{fmt(data.totalAmount)}</span></div>
            {data.amountPaid !== undefined && <div className="flex justify-between py-0.5 px-2.5"><span className="text-gray-500">Paid</span><span className="font-mono text-green-700">{fmt(data.amountPaid)}</span></div>}
          </div>
        </div>
      )}

      {(data.documentSubtype === 'CROSSMATCH_REPORT' || data.documentSubtype === 'BLOOD_BANK_INVOICE') && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[8px] text-amber-800">
          Placeholder — Full {SUBTYPE_LABELS[data.documentSubtype]?.toLowerCase()} requires integration with blood bank management system.
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
